import { Entity } from '@api/api/entity-schema';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { RangeHorizon } from '@helpers/utils/dates/dates';
import { RowSelectionState } from '@tanstack/react-table';
import { ErrorMessageWrapper } from '@terminal/components/error-message-wrapper/ErrorMessageWrapper';
import { PriceMovementChart } from '@terminal/components/price-movement/components/price-movement-chart/PriceMovementChart';
import { useScenarios } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { getPredictionsTableData } from '@terminal/views/turbo-chart/components/insights/utils/insights-utils/insights-utils';
import { getMarketHoursLabel } from '@terminal/widgets/earnings/utils/utils';
import { PredictionSummaryTable } from '@terminal/widgets/scenario/components/prediction-summary-table/PredictionSummaryTable';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEarningsStore } from '../../useEarningsStore/useEarningsStore';
import styles from './EarningsPriceMovementContent.module.scss';
import { getQuarterTooltipText } from './utils';

const HORIZON_TO_INDEX = new Map<string, number>([
  [RangeHorizon.OneDay, 1],
  [RangeHorizon.TwoDays, 2],
  [RangeHorizon.OneWeek, 4],
  [RangeHorizon.TwoWeeks, 9],
  [RangeHorizon.OneMonth, 20],
]);

const HORIZONS = [...HORIZON_TO_INDEX.keys()];

export interface EarningsPriceMovementContentProps {
  rowSelection: RowSelectionState;
  entity: Entity;
}

export const EarningsPriceMovementContent = ({
  rowSelection,
  entity,
}: EarningsPriceMovementContentProps) => {
  const { t } = useTranslation(['earnings', 'widget']);

  const { earningsData } = useEarningsStore();
  const { data: scenarios, isLoading, error, sendMessage } = useScenarios();

  const scenario = scenarios[0];
  const selectedReportingDates = Object.keys(rowSelection).filter(
    key => rowSelection[key]
  );
  const selectedRowsLength = selectedReportingDates.length;
  const selectedEarnings = earningsData.filter(earning =>
    selectedReportingDates.includes(earning.reporting_date)
  );

  useEffect(() => {
    if (!selectedRowsLength) {
      return;
    }

    const reportingDates = Object.keys(rowSelection).filter(
      key => rowSelection[key]
    );

    sendMessage({
      entities: [
        {
          tag: entity.tag,
          snake: entity.default_snake,
        },
      ],
      events: reportingDates.map(reportingDate => ({
        date: reportingDate,
        market_hours: getMarketHoursLabel(new Date(reportingDate)),
      })),
      horizons: HORIZONS,
      start_horizon: '0d',
      // set '1m' to align with the <EarningsPriceMovementsChartAccordion />
      end_horizon: '1m',
      skip_report: true,
    });
  }, [rowSelection, entity, sendMessage, selectedRowsLength]);

  if (!selectedRowsLength) {
    return null;
  }

  if (error) {
    return <ErrorMessageWrapper variant="error" />;
  }

  if (isLoading || !scenario) {
    return (
      <SkeletonLoader
        data-testid="earnings-price-movement-loading"
        areas={[
          { styles: { width: '100%', height: 312, borderRadius: '8px' } },
        ]}
      />
    );
  }

  const predictionsTableData = getPredictionsTableData(
    HORIZONS,
    scenario.predictions
  );

  return (
    <>
      <div className={styles.chartTitleLabel}>
        <span>
          {t('widget:earnings.earningsScenario.priceMovementsFollowing')}
          <span className={styles.highlightedValue}>
            {` ${selectedRowsLength} `}
          </span>
          {t('widget:earnings.earningsScenario.selectedEarningsReleases', {
            count: selectedRowsLength,
          })}
        </span>
      </div>
      <PriceMovementChart
        prediction={scenario.predictions.map(p => ({
          high: p.high,
          median: p.median,
          low: p.low,
          relative_idx: p.relative_idx,
        }))}
        defaultHorizonToIndex={HORIZON_TO_INDEX}
        defaultHorizons={HORIZONS}
        yAxisLabel={t('widget:earnings.return%')}
        useDynamicDaysLabel
      />
      <div>
        <h4 className={styles.predictionSummaryTableTitle}>
          {t('earnings:outliers')}
        </h4>
        <div className={styles.tableWrapper}>
          <PredictionSummaryTable
            data={predictionsTableData}
            isMedian
            horizons={HORIZONS}
            isFixedIncome={false}
            getTooltipText={params =>
              getQuarterTooltipText({
                ...params,
                selectedEarnings,
              })
            }
          />
        </div>
      </div>
    </>
  );
};
